import React, { useContext, useEffect, useState, useRef, useCallback } from 'react';
import classnames from 'classnames';
import { faCaretRight, faCaretLeft, faInfoCircle, faSyncAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ListGroup, Button, Spinner } from 'react-bootstrap';
import AlertDetail from '../AlertDetail';
import { useFetchMessages } from '../../hook';
import I18N, { I18NContext } from '../../common/I18N';
import api from '../../api';

import './AlertList.scss'
import { Alert } from '../../type';
import { WebWidgetContext, WEB_WIDGET_ACTIONS } from '../../context';

interface AlertListProps {
  isOpen: boolean,
  enableMapDisplay: boolean
}

const AlertList: React.FC<AlertListProps> = ({ isOpen, enableMapDisplay }) => {
  const {
    orgId,
    stream,
    actionDispatch,
    state: {
      selectedAlert
    }
  } = useContext(WebWidgetContext);

  const [open, setOpen] = useState(isOpen);
  const [baseTime, updateBaseTime] = useState(0);

  const [currentSelectedAlert, setCurrentSelectedAlert] = useState({ notificationId: '' })

  const listRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (selectedAlert) {
      setCurrentSelectedAlert(selectedAlert)
    }
  }, [selectedAlert])

  const handleClickFloatBar = () => {
    setOpen(!open);
  }

  const handleClickListItem = (alert: Alert) => {
    if (!enableMapDisplay) return
    if (alert.hasPolygon) {
      actionDispatch(WEB_WIDGET_ACTIONS.FETCH_ALERT_DETAIL, null);
      api.getMessageDetail(orgId, alert.notificationId).then(({ data, status }) => {
        if (status === 200) {
          const { polygon } = data;
          alert.polygon = polygon;
          actionDispatch(WEB_WIDGET_ACTIONS.SELECT_ALERT, alert);
        }
      })
        .catch(() => {
          // Workaround for handle expired message
          alert.polygon = null;
          actionDispatch(WEB_WIDGET_ACTIONS.SELECT_ALERT, alert);
          actionDispatch(WEB_WIDGET_ACTIONS.SHOW_NO_SHAPE_MSG, true);
        })
        .finally(() => {
          actionDispatch(WEB_WIDGET_ACTIONS.FETCH_ALERT_DETAIL_DONE, null);
        });
    } else {
      alert.polygon = null;
      actionDispatch(WEB_WIDGET_ACTIONS.SELECT_ALERT, alert);
      actionDispatch(WEB_WIDGET_ACTIONS.SHOW_NO_SHAPE_MSG, true);
    }
  }

  const [isLoading, messages, fetchMessages] = useFetchMessages(orgId, 0, stream);
  const [messageList, updateMessageList] = useState(messages);
  const getMessages = () => {
    if (listRef.current) {
      listRef.current.removeEventListener('scroll', onScroll);
    }
    api.getMessages(orgId, baseTime, stream).then(response => {
      const { status, data: { messages } } = response
      if (status === 200 && messages.length) {
        updateMessageList([...messageList, ...messages]);
        const newBaseTime = messages[messages.length - 1].createDate;
        if (newBaseTime !== baseTime) {
          updateBaseTime(messages[messages.length - 1].createDate);
        }
      }
    }).catch(() => {
      // Keep the current list when pagination fails.
    }).finally(() => {
      if (listRef.current) {
        listRef.current.addEventListener('scroll', onScroll);
      }
    })
  }
  const onScroll = (event: any) => {
    const clientHeight = event.target.clientHeight
    const scrollHeight = event.target.scrollHeight
    const scrollTop = event.target.scrollTop
    if (clientHeight + scrollTop >= scrollHeight - 40) {
      getMessages();
    }
  };

  const handleRefresh = () => {
    fetchMessages().catch(() => undefined);
  }

  useEffect(() => {
    fetchMessages().catch(() => undefined);
  }, [])

  useEffect(() => {
    if (messages && baseTime === 0 && messages.length > 0) {
      const firstMessage = messages[0];
      if (firstMessage.hasPolygon) {
        handleClickListItem(firstMessage);
      }
    }
  }, [messages, baseTime]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.addEventListener('scroll', onScroll);
    }
    return () => {
      if (listRef.current) {
        listRef.current.removeEventListener('scroll', onScroll);
      }
    };
  }, [baseTime])

  useEffect(() => {
    updateMessageList(messages);
    if (messages.length) {
      updateBaseTime(messages[messages.length - 1].createDate);
      if (listRef.current) {
        listRef.current.scrollTop = 0;
      }
    }
  }, [messages])

  const i18n: any = useContext(I18NContext)

  return (
    <div className={classnames('alerts', open ? 'alerts-expand' : '')}>
      <button type="button" className="alerts-float-bar" onClick={handleClickFloatBar}>
        {
          open ? <FontAwesomeIcon icon={faCaretLeft} /> : <FontAwesomeIcon icon={faCaretRight} />
        }
      </button>
      <div className={classnames('alerts-content', 'd-flex', 'flex-column', open ? 'alerts-content-show' : '')}>
        <header className="container">
          <div className="row align-items-end justify-content-between">
            <div className="col-auto pb-2 alerts-list-title"><I18N>web.widget.messages</I18N></div>
            <div className="col-auto pb-2 text-right alerts-list-logo">
              <div><I18N>web.widget.powered.by</I18N></div>
              <div>
                <i className="evbg-logo"></i>
              </div>
            </div>
          </div>
        </header>
        <div className="text-center refresh-area">
          <Button variant="link" onClick={handleRefresh} disabled={isLoading}>
            <FontAwesomeIcon icon={faSyncAlt} className="refresh-icon" />
          </Button>
        </div>
        <div className="alerts-content-list" ref={listRef}>
          {
            isLoading ?
              <div className="text-center alerts-content-loading"> <Spinner animation="grow" variant="primary" /></div>
              :
              <ListGroup variant="flush" className="px-0">
                {messageList.map((alert) => {
                  return (
                    <ListGroup.Item
                      as="div"
                      action
                      className="alerts-list-item"
                      key={alert.notificationId}
                      active={alert.notificationId === currentSelectedAlert.notificationId}
                      onClick={() => handleClickListItem(alert)}
                    >
                      <AlertDetail alert={alert} isShowFullContent={false} />
                    </ListGroup.Item>
                  );
                })
                }
                {!messageList.length &&
                  <ListGroup.Item className="no-alert">
                    <div>
                      <FontAwesomeIcon icon={faInfoCircle} className="no-alert-icon" />
                      <span className="no-alert-message"><I18N>web.widget.no.message</I18N></span>
                    </div>
                  </ListGroup.Item>
                }
              </ListGroup>
          }
        </div>
      </div>
    </div >
  )
}

export default AlertList;
